import React from 'react';
import {
  Eyecatcher,
  EyecatcherType,
} from 'js/pages/BrowsePage/BrowseResult/Eyecatcher';
import { VenueTag, VenueTagData } from 'js/components/VenueTag/VenueTag';
import { BestTreatmentsBadge } from 'js/components/BestTreatmentsBadge';
import { VenueRating } from 'js/model/rainbow/venue/VenueOutput';
import { BestTreatmentsBadgeStyle } from 'js/components/BestTreatmentsBadge/BestTreatmentsBadge';
import { trackBestTreatmentsBadgeViewed } from 'js/pages/BrowsePage/BrowseResult/tracking';
import { Stack, Inline, Text, Viewport, useDevice } from '@treatwell/ui';
import { Image } from 'src/js/model/rainbow/ImageOutput';
import { InView } from 'react-intersection-observer';
import { BrowseResultRating } from './BrowseResultRating';
import { BrowseResultImageCarousel } from './BrowseResultImageCarousel';
import styles from './BrowseResultSummary.module.css';

interface Props {
  id: number;
  name: string;
  address: string;
  distance?: string;
  rating: VenueRating;
  openMapText: string;
  images: Image[];
  eyecatcher?: EyecatcherType;
  tags: VenueTagData[];
  onLocationClick?: (event: React.MouseEvent) => void;
  bestTreatmentsBadgeLabel?: string;
  bestTreatmentsBadgeTooltip: string;
  bestTreatmentsTrackingIds: number[];
  reviewCountTemplate: string;
  isInHybridView?: boolean;
  isLazy: boolean;
}

export const BrowseResultSummary = ({
  images,
  isInHybridView,
  eyecatcher,
  bestTreatmentsBadgeLabel,
  bestTreatmentsTrackingIds,
  id,
  bestTreatmentsBadgeTooltip,
  name,
  rating,
  reviewCountTemplate,
  onLocationClick,
  address,
  distance,
  openMapText,
  tags,
  isLazy,
}: Props) => {
  const { isMobile } = useDevice();

  const trackBestTreatmentsBadgeHasBeenViewed = (inView: boolean) => {
    if (inView) {
      bestTreatmentsTrackingIds.forEach((trackingId) => {
        trackBestTreatmentsBadgeViewed(id, trackingId);
      });
    }
  };

  const toRender = () => (
    <>
      <div className={styles.imageAreaContainer}>
        <div className={styles.imageContainer}>
          <BrowseResultImageCarousel
            positioningClassName={styles.imageGallery}
            images={images}
            isInHybridView={isInHybridView}
            isLazy={isLazy}
          />
          {eyecatcher && <Eyecatcher data={eyecatcher} />}
        </div>
      </div>
      <div className={styles.textContainer}>
        <Stack space="xs">
          {bestTreatmentsBadgeLabel !== undefined && (
            <div className={styles.bestTreatmentsBadgeWrapper}>
              <InView
                triggerOnce
                onChange={trackBestTreatmentsBadgeHasBeenViewed}
              >
                <BestTreatmentsBadge
                  label={bestTreatmentsBadgeLabel}
                  style={BestTreatmentsBadgeStyle.Solid}
                  tooltip={bestTreatmentsBadgeTooltip}
                />
              </InView>
            </div>
          )}
          <div className={styles.name}>
            <Text type="mdHeader" as="h2">
              {name}
            </Text>
          </div>
          <BrowseResultRating
            rating={rating}
            reviewCountTemplate={reviewCountTemplate}
          />

          <Inline wrap className={styles.location} onClick={onLocationClick}>
            <div className={styles.label}>
              <Text>{address}</Text>
            </div>
            {distance && <span className={styles.distance}> ({distance})</span>}
            {!isInHybridView && (
              <Text type="bodyHeavy" className={styles.openMap}>
                {openMapText}
              </Text>
            )}
          </Inline>

          {tags.map((tagData) => (
            <div className={styles.venueTag} key={tagData.label}>
              <VenueTag {...tagData} size="large" />
            </div>
          ))}
        </Stack>
      </div>
    </>
  );

  return (
    <>
      <Viewport serverRender={isMobile} device="mobile">
        <Stack space="md">{toRender()}</Stack>
      </Viewport>
      <Viewport device={['tablet', 'desktop']} serverRender={!isMobile}>
        <Inline className={styles.inline} space="md" align="start">
          {toRender()}
        </Inline>
      </Viewport>
    </>
  );
};
